import { createFileRoute, useNavigate, stripSearchParams } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import { supabase } from "@/integrations/supabase/client";
import { supabaseAdmin } from "@/integrations/supabase/client.server";
import { CarCard } from "@/components/CarCard";
import { HeroBanner } from "@/components/HeroBanner";
import { useHeroSettings } from "@/hooks/use-hero-settings";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { Search, X, Sparkles, Loader2, SlidersHorizontal } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { z } from "zod";
import { zodValidator, fallback } from "@tanstack/zod-adapter";
import { useServerFn } from "@tanstack/react-start";
import { aiCarSearch } from "@/lib/ai-search.functions";
import { trackCarSearch } from "@/lib/search-tracking.functions";
import { trackConversion } from "@/lib/pixel";
import { getGaClientId } from "@/lib/ga-client";
import { getAttributionForEvent } from "@/lib/attribution";
import { toast } from "sonner";
import { buildBreadcrumbJsonLd } from "@/lib/breadcrumb-jsonld";
import { buildSeoMeta } from "@/lib/seo";
import { buildCarCollectionJsonLd } from "@/lib/jsonld";
import { RefreshCw } from "lucide-react";
import { formatSyncedHint } from "@/lib/utils";
import { fetchCarsManifest, type CarsManifest } from "@/lib/car-image-manifest";

const BREADCRUMB_JSON_LD = buildBreadcrumbJsonLd([
  { name: "Forside", path: "" },
  { name: "Biler til salg", path: "biler-til-salg" },
]);

// SSR-only fetch of the current inventory snapshot used by the CollectionPage
// JSON-LD. Bypasses RLS via the admin client so crawlers see the same list
// anonymous visitors see, and stays narrow (50 rows, summary fields) to keep
// the structured-data payload light.
const fetchCarsForCollection = createServerFn({ method: "GET" }).handler(async () => {
  const { data } = await supabaseAdmin
    .from("cars")
    .select("id, brand, model, variant, year, price, images, seo_slug")
    .eq("status", "active")
    .eq("is_demo", false)
    .order("created_at", { ascending: false })
    .limit(50);
  return (data || []).map((c) => ({
    id: c.id,
    slug: c.seo_slug,
    brand: c.brand,
    model: c.model,
    variant: c.variant,
    year: c.year,
    price: c.price,
    image: c.images && c.images.length > 0 ? c.images[0] : null,
  }));
});

const searchSchema = z.object({
  q: fallback(z.string(), "").default(""),
  brand: fallback(z.string(), "").default(""),
  fuel: fallback(z.string(), "").default(""),
  body: fallback(z.string(), "").default(""),
  sort: fallback(z.string(), "alle").default("alle"),
  priceMin: fallback(z.number(), 0).default(0),
  priceMax: fallback(z.number(), 0).default(0),
  kmMin: fallback(z.number(), 0).default(0),
  kmMax: fallback(z.number(), 0).default(0),
  yearMin: fallback(z.number(), 0).default(0),
  yearMax: fallback(z.number(), 0).default(0),
  seatsMin: fallback(z.number(), 0).default(0),
  filters: fallback(z.boolean(), false).default(false),
});

const searchDefaults = { q: "", brand: "", fuel: "", body: "", sort: "alle", priceMin: 0, priceMax: 0, kmMin: 0, kmMax: 0, yearMin: 0, yearMax: 0, seatsMin: 0, filters: false };

// Body-type slug ↔ display label mapping. Keep slugs short, lowercase, and
// hyphenated so URLs like ?body=stationcar stay readable & SEO-friendly.
// Matching is normalized (lowercase + diacritics stripped) against the raw
// `body_type` column so casing/spelling variants from Bilinfo still match.
const BODY_TYPE_OPTIONS: { slug: string; label: string }[] = [
  { slug: "sedan", label: "Sedan" },
  { slug: "suv", label: "SUV" },
  { slug: "stationcar", label: "Stationcar" },
  { slug: "hatchback", label: "Hatchback" },
  { slug: "coupe", label: "Coupé" },
  { slug: "cabriolet", label: "Cabriolet" },
  { slug: "mpv", label: "MPV" },
  { slug: "varebil", label: "Varebil" },
  { slug: "pickup", label: "Pickup" },
];

function normalizeBodyType(raw: string | null | undefined): string {
  if (!raw) return "";
  return raw
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim();
}

export const Route = createFileRoute("/biler-til-salg/")({
  validateSearch: zodValidator(searchSchema),
  search: {
    middlewares: [stripSearchParams(searchDefaults)],
  },
  loader: async () => {
    try {
      const cars = await fetchCarsForCollection();
      return { cars };
    } catch {
      return { cars: [] as Awaited<ReturnType<typeof fetchCarsForCollection>> };
    }
  },
  head: ({ loaderData }) => {
    const title: string = "Biler til salg · AutoXChange Børkop";
    const description: string =
      "Se vores aktuelle udvalg af håndplukkede brugte biler i Børkop. Filtrér på mærke, brændstof, pris og km — alle biler er gennemgået og klar til prøvetur.";
    const seo = buildSeoMeta({
      title: title,
      description: description,
      path: "/biler-til-salg",
    });
    return {
      ...seo,
      scripts: [
        {
          type: "application/ld+json",
          children: buildCarCollectionJsonLd({
            title: title,
            description: description,
            cars: loaderData?.cars ?? [],
          }),
        },
      ],
    };
  },
  component: BilerTilSalg,
});

function BilerTilSalg() {
  const { q, brand, fuel, body, sort, priceMin, priceMax, kmMin, kmMax, yearMin, yearMax, seatsMin, filters } = Route.useSearch();
  const navigate = useNavigate({ from: "/biler-til-salg/" });
  const [aiQuery, setAiQuery] = useState(q);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiSummary, setAiSummary] = useState<string | null>(null);
  const aiSearchFn = useServerFn(aiCarSearch);
  const trackSearchFn = useServerFn(trackCarSearch);
  const { data: hero } = useHeroSettings("cars");

  const setParam = (updates: Record<string, unknown>) => {
    navigate({ search: (prev: Record<string, unknown>) => ({ ...prev, ...updates }), replace: true });
  };

  const runAiSearch = async () => {
    const query = aiQuery.trim();
    if (!query || aiLoading) return;
    setAiLoading(true);
    try {
      const result = await aiSearchFn({ data: { query } });
      if (result.error) {
        toast.error(result.error);
        return;
      }
      if (result.filters) {
        const f = result.filters;
        setParam({
          q: f.searchTerm,
          brand: f.brand,
          fuel: f.fuelType,
          priceMin: f.minPrice,
          priceMax: f.maxPrice,
          kmMax: f.maxMileage,
          yearMin: f.minYear,
          seatsMin: f.minSeats,
          sort: f.sort,
        });
        setAiSummary(result.summary);
        toast.success("Filtre opdateret med AI");
      }
    } catch (err) {
      console.error(err);
      toast.error("AI-søgning fejlede");
    } finally {
      setAiLoading(false);
    }
  };

  const { data: cars, isLoading } = useQuery({
    queryKey: ["cars"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("cars")
        .select("id, brand, model, variant, year, mileage, price, fuel_type, transmission, color, body_type, doors, seats, engine_size, horsepower, description, equipment, images, status, is_new, is_popular, is_leasing, leasing_price, is_demo, first_registration, seo_title, seo_description, seo_slug, created_at")
        .eq("status", "active")
        .eq("is_demo", false)
        .order("created_at", { ascending: false });
      if (error) {
        console.error("Failed to load cars:", error);
        toast.error("Kunne ikke hente biler", {
          description: error.message,
        });
        throw error;
      }
      return data || [];
    },
    retry: 1,
  });

  // Fetch the multi-format derivative manifest for the visible cars in
  // one round-trip. Keyed off the car id list so it refetches whenever
  // a car appears or disappears (e.g. status flip → 'sold'). Cars without
  // manifest rows simply fall back to the legacy <img> srcset path inside
  // CarCard, so this query never blocks the listing render.
  const carIdsKey = useMemo(() => (cars ?? []).map((c) => c.id).sort().join(","), [cars]);
  const { data: manifest } = useQuery<CarsManifest>({
    queryKey: ["cars", "manifest", carIdsKey],
    enabled: !!cars && cars.length > 0,
    queryFn: () => fetchCarsManifest((cars ?? []).map((c) => c.id)),
    staleTime: 5 * 60 * 1000, // Manifest entries are immutable per upload.
  });

  // Most recent Bilinfo sync timestamp — drives the "Opdateret X siden" trust marker.
  const { data: lastSyncedAt } = useQuery({
    queryKey: ["biler-til-salg", "last-synced-at"],
    queryFn: async () => {
      const { data } = await supabase
        .from("cars")
        .select("last_synced_at")
        .not("last_synced_at", "is", null)
        .order("last_synced_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      return data?.last_synced_at ?? null;
    },
    staleTime: 60 * 1000,
    refetchInterval: 60 * 1000,
  });

  const priceExtent = useMemo(() => {
    if (!cars || cars.length === 0) return { min: 0, max: 1000000 };
    const prices = cars.map((c) => c.price);
    return { min: Math.min(...prices), max: Math.max(...prices) };
  }, [cars]);

  const mileageExtent = useMemo(() => {
    if (!cars || cars.length === 0) return { min: 0, max: 500000 };
    const mileages = cars.map((c) => c.mileage);
    return { min: Math.min(...mileages), max: Math.max(...mileages) };
  }, [cars]);

  const yearExtent = useMemo(() => {
    if (!cars || cars.length === 0) return { min: 2000, max: new Date().getFullYear() };
    const years = cars.map((c) => c.year);
    return { min: Math.min(...years), max: Math.max(...years) };
  }, [cars]);

  const activeMin = priceMin || priceExtent.min;
  const activeMax = priceMax || priceExtent.max;
  const activeKmMin = kmMin || mileageExtent.min;
  const activeKmMax = kmMax || mileageExtent.max;
  const activeYearMin = yearMin || yearExtent.min;
  const activeYearMax = yearMax || yearExtent.max;

  const formatPrice = (n: number) => new Intl.NumberFormat("da-DK").format(n);

  const filteredCars = (cars || []).filter((car) => {
    const matchesSearch = !q || `${car.brand} ${car.model} ${car.variant || ""}`.toLowerCase().includes(q.toLowerCase());
    const matchesBrand = !brand || car.brand === brand;
    const matchesFuel = !fuel || car.fuel_type === fuel;
    const matchesBody = !body || normalizeBodyType(car.body_type) === body;
    const matchesPrice = car.price >= activeMin && car.price <= activeMax;
    const matchesMileage = car.mileage >= activeKmMin && car.mileage <= activeKmMax;
    const matchesYear = car.year >= activeYearMin && car.year <= activeYearMax;
    const matchesSeats = !seatsMin || (car.seats ?? 0) >= seatsMin;
    return matchesSearch && matchesBrand && matchesFuel && matchesBody && matchesPrice && matchesMileage && matchesYear && matchesSeats;
  }).sort((a, b) => {
    if (sort === "price_asc") return a.price - b.price;
    if (sort === "price_desc") return b.price - a.price;
    if (sort === "year") return b.year - a.year;
    if (sort === "mileage") return a.mileage - b.mileage;
    return 0;
  });

  const brands = [...new Set((cars || []).map((c) => c.brand))].sort();
  const fuelTypes = [...new Set((cars || []).map((c) => c.fuel_type))];

  // Only show body-type options that actually exist in the current inventory,
  // so the dropdown never offers filters that would return zero results.
  const availableBodySlugs = useMemo(() => {
    const present = new Set((cars || []).map((c) => normalizeBodyType(c.body_type)).filter(Boolean));
    return BODY_TYPE_OPTIONS.filter((o) => present.has(o.slug));
  }, [cars]);

  const fuelLabels: Record<string, string> = {
    benzin: "Benzin", diesel: "Diesel", el: "El", hybrid: "Hybrid", plugin_hybrid: "Plugin Hybrid",
  };

  const resetFilters = () => {
    setParam({ q: "", brand: "", fuel: "", body: "", sort: "alle", priceMin: 0, priceMax: 0, kmMin: 0, kmMax: 0, yearMin: 0, yearMax: 0, seatsMin: 0 });
    setAiSummary(null);
    setAiQuery("");
  };

  const activeFilterCount = [
    brand,
    fuel,
    body,
    priceMin > 0,
    priceMax > 0,
    kmMin > 0,
    kmMax > 0,
    yearMin > 0,
    yearMax > 0,
  ].filter(Boolean).length;

  // Debounced Search conversion event — fires once a visitor stops interacting
  // for 800ms with a non-trivial query or active filter. Sends to Meta CAPI +
  // GA4 MP server-side (no PII) and to the browser pixel/gtag with a shared
  // event_id for deduplication. Skips firing on the initial empty state.
  const lastSearchSigRef = useRef<string | null>(null);
  useEffect(() => {
    if (isLoading) return;
    const trimmedQ = q.trim();
    const hasFilter = !!brand || !!fuel;
    const hasQuery = trimmedQ.length >= 2;
    if (!hasFilter && !hasQuery) return;

    // Dedup: only fire once per unique combination of search inputs.
    const sig = JSON.stringify({ q: trimmedQ, brand, fuel });
    if (lastSearchSigRef.current === sig) return;

    const handle = window.setTimeout(() => {
      lastSearchSigRef.current = sig;
      const sourceUrl = window.location.href;
      const gaClientId = getGaClientId() ?? undefined;
      const resultCount = filteredCars.length;

      (async () => {
        let eventId: string | undefined;
        try {
          const res = await trackSearchFn({
            data: {
              query: trimmedQ,
              brand: brand || undefined,
              fuel: fuel || undefined,
              resultCount,
              sourceUrl,
              gaClientId,
              attribution: getAttributionForEvent(),
            },
          });
          eventId = res?.eventId;
        } catch (e) {
          console.error("Search server fn failed:", e);
        }

        trackConversion({
          meta: {
            event: "Search",
            params: {
              search_string: trimmedQ || undefined,
              content_category: "car",
              content_type: "vehicle",
              ...(brand ? { brand } : {}),
              ...(fuel ? { fuel_type: fuel } : {}),
              num_items: resultCount,
            },
            eventId,
          },
          ga: {
            event: "view_search_results",
            params: {
              event_id: eventId,
              search_term: trimmedQ || undefined,
              ...(brand ? { brand } : {}),
              ...(fuel ? { fuel_type: fuel } : {}),
              num_results: resultCount,
            },
          },
        });
      })();
    }, 800);

    return () => window.clearTimeout(handle);
  }, [q, brand, fuel, isLoading, filteredCars.length, trackSearchFn]);


  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: BREADCRUMB_JSON_LD }} />
      <HeroBanner
        title="Vores aktuelle biler"
        subtitle="Alle biler er gennemgået og klar til prøvetur — kom forbi pladsen i Børkop"
        image={hero?.url}
        mediaType={hero?.mediaType}
      />

      <section className="bg-background py-12">
        <div className="mx-auto max-w-7xl px-6">
          {/* Combined search box: AI search + sort + filters */}
          <div className="mb-8 rounded-lg border border-border bg-card p-6">
            {/* AI / keyword search */}
            <div className="mb-2 flex items-center gap-2">
              <Sparkles size={16} className="text-accent" />
              <span className="text-sm font-semibold text-foreground">Søg efter din næste bil</span>
              <span className="hidden text-xs text-muted-foreground sm:inline">
                — skriv et mærke eller beskriv hvad du leder efter
              </span>
            </div>
            <div className="flex flex-col gap-2 sm:flex-row">
              <div className="relative flex-1">
                <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder='Fx "Tesla" eller "familiebil under 250.000 kr med plads til 7"'
                  value={aiQuery}
                  onChange={(e) => {
                    setAiQuery(e.target.value);
                    setParam({ q: e.target.value });
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      const trimmed = aiQuery.trim();
                      const looksLikePrompt = trimmed.split(/\s+/).length >= 3 || /\d/.test(trimmed);
                      if (looksLikePrompt) runAiSearch();
                    }
                  }}
                  disabled={aiLoading}
                  className="bg-background pl-10 pr-10"
                />
                {aiQuery && (
                  <button
                    onClick={() => {
                      setAiQuery("");
                      setParam({ q: "" });
                    }}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    aria-label="Ryd søgning"
                  >
                    <X size={16} />
                  </button>
                )}
              </div>
              <Button
                onClick={runAiSearch}
                disabled={aiLoading || aiQuery.trim().length < 2}
                className="gap-2 bg-accent text-accent-foreground hover:brightness-110"
              >
                {aiLoading ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
                {aiLoading ? "Søger..." : "Find min bil"}
              </Button>
            </div>
            <p className="mt-2 text-[11px] text-muted-foreground">
              Tip: Skriv et mærke for hurtig filtrering, eller en hel sætning og tryk
              <span className="mx-1 font-medium text-foreground">Find min bil</span>
              for at lade AI sætte filtrene automatisk.
            </p>
            {aiSummary && (
              <div className="mt-3 flex items-start justify-between gap-3 rounded-lg bg-background/60 px-3 py-2 text-sm text-muted-foreground">
                <span><span className="font-medium text-foreground">AI tolkede:</span> {aiSummary}</span>
                <button onClick={() => setAiSummary(null)} className="text-muted-foreground hover:text-foreground" aria-label="Luk">
                  <X size={14} />
                </button>
              </div>
            )}

            {/* Divider */}
            <div className="my-5 border-t border-border" />

            {/* Sort + mobile filter trigger */}
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
              {/* Mobile-only: opens filter drawer. Desktop shows filters inline below. */}
              <Sheet>
                <SheetTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-2 lg:hidden"
                    aria-label="Åbn filtre"
                  >
                    <SlidersHorizontal size={16} />
                    Filtre
                    {activeFilterCount > 0 && (
                      <Badge variant="secondary" className="ml-1 h-5 min-w-5 px-1.5">
                        {activeFilterCount}
                      </Badge>
                    )}
                  </Button>
                </SheetTrigger>
                <SheetContent
                  side="right"
                  className="w-full overflow-y-auto sm:max-w-md"
                >
                  <SheetHeader>
                    <SheetTitle>Filtre</SheetTitle>
                    <SheetDescription>
                      Indsnævr søgningen — ændringer gemmes automatisk i URL'en.
                    </SheetDescription>
                  </SheetHeader>
                  <div className="mt-6">
                    <FilterControls
                      brand={brand}
                      fuel={fuel}
                      body={body}
                      brands={brands}
                      fuelTypes={fuelTypes}
                      fuelLabels={fuelLabels}
                      bodyOptions={availableBodySlugs}
                      priceExtent={priceExtent}
                      mileageExtent={mileageExtent}
                      yearExtent={yearExtent}
                      activeMin={activeMin}
                      activeMax={activeMax}
                      activeKmMin={activeKmMin}
                      activeKmMax={activeKmMax}
                      activeYearMin={activeYearMin}
                      activeYearMax={activeYearMax}
                      formatPrice={formatPrice}
                      setParam={setParam}
                      layout="stacked"
                    />
                  </div>
                  <SheetFooter className="mt-6">
                    <Button
                      variant="outline"
                      onClick={resetFilters}
                      className="w-full"
                    >
                      Nulstil filtre
                    </Button>
                  </SheetFooter>
                </SheetContent>
              </Sheet>

              <div className="ml-auto flex items-center gap-3">
                <label className="text-sm text-muted-foreground">Sortér:</label>
                <select
                  value={sort}
                  onChange={(e) => setParam({ sort: e.target.value })}
                  className="rounded-md border border-border bg-background px-4 py-2 text-sm"
                >
                  <option value="alle">Alle</option>
                  <option value="price_asc">Pris (Lav → Høj)</option>
                  <option value="price_desc">Pris (Høj → Lav)</option>
                  <option value="year">Nyeste først</option>
                  <option value="mileage">Laveste km</option>
                </select>
              </div>
            </div>

            {/* Desktop-only inline filters */}
            <div className="hidden lg:block">
              <FilterControls
                brand={brand}
                fuel={fuel}
                body={body}
                brands={brands}
                fuelTypes={fuelTypes}
                fuelLabels={fuelLabels}
                bodyOptions={availableBodySlugs}
                priceExtent={priceExtent}
                mileageExtent={mileageExtent}
                yearExtent={yearExtent}
                activeMin={activeMin}
                activeMax={activeMax}
                activeKmMin={activeKmMin}
                activeKmMax={activeKmMax}
                activeYearMin={activeYearMin}
                activeYearMax={activeYearMax}
                formatPrice={formatPrice}
                setParam={setParam}
                layout="grid"
                onReset={resetFilters}
              />
            </div>
          </div>

          <div className="mb-6 flex flex-wrap items-center justify-between gap-2">
            <p className="text-sm text-muted-foreground">Viser {filteredCars.length} biler</p>
            <p className="inline-flex items-center gap-1.5 text-xs text-muted-foreground/80">
              <RefreshCw size={12} className="text-accent" aria-hidden />
              {formatSyncedHint(lastSyncedAt)}
            </p>
          </div>

          {isLoading ? (
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="aspect-[4/3] animate-pulse rounded-lg bg-muted" />
              ))}
            </div>
          ) : filteredCars.length > 0 ? (
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {filteredCars.map((car) => (
                <CarCard key={car.id} {...car} manifest={manifest?.[car.id]} />
              ))}
            </div>
          ) : (
            <div className="py-20 text-center">
              <p className="text-lg font-medium text-muted-foreground">Ingen biler matcher dine kriterier</p>
              <p className="mt-2 text-sm text-muted-foreground">Prøv at justere filtrene, eller kontakt os — måske har vi noget på vej ind.</p>
            </div>
          )}
        </div>
      </section>
    </>
  );
}

// Shared filter controls — rendered inline (grid) on desktop and stacked
// inside the mobile Sheet. All onChange handlers route through `setParam`,
// which is the single source of truth that syncs to the URL search params,
// so the mobile drawer needs no separate state management.
type FilterControlsProps = {
  brand: string;
  fuel: string;
  body: string;
  brands: string[];
  fuelTypes: (string | null)[];
  fuelLabels: Record<string, string>;
  bodyOptions: { slug: string; label: string }[];
  priceExtent: { min: number; max: number };
  mileageExtent: { min: number; max: number };
  yearExtent: { min: number; max: number };
  activeMin: number;
  activeMax: number;
  activeKmMin: number;
  activeKmMax: number;
  activeYearMin: number;
  activeYearMax: number;
  formatPrice: (n: number) => string;
  setParam: (updates: Record<string, unknown>) => void;
  layout: "grid" | "stacked";
  onReset?: () => void;
};

function FilterControls({
  brand,
  fuel,
  body,
  brands,
  fuelTypes,
  fuelLabels,
  bodyOptions,
  priceExtent,
  mileageExtent,
  yearExtent,
  activeMin,
  activeMax,
  activeKmMin,
  activeKmMax,
  activeYearMin,
  activeYearMax,
  formatPrice,
  setParam,
  layout,
  onReset,
}: FilterControlsProps) {
  const wrapperClass = layout === "grid"
    ? "grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6"
    : "flex flex-col gap-5";

  return (
    <div className={wrapperClass}>
      <div>
        <label className="mb-1 block text-sm font-medium text-foreground">Fabrikant</label>
        <select
          value={brand}
          onChange={(e) => setParam({ brand: e.target.value })}
          className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
        >
          <option value="">Alle</option>
          {brands.map((b) => <option key={b} value={b}>{b}</option>)}
        </select>
      </div>
      <div>
        <label className="mb-1 block text-sm font-medium text-foreground">Brændstof</label>
        <select
          value={fuel}
          onChange={(e) => setParam({ fuel: e.target.value })}
          className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
        >
          <option value="">Alle</option>
          {fuelTypes.filter((f): f is string => !!f).map((f) => (
            <option key={f} value={f}>{fuelLabels[f] || f}</option>
          ))}
        </select>
      </div>
      <div>
        <label htmlFor="body-type-filter" className="mb-1 block text-sm font-medium text-foreground">Karrosseri</label>
        <select
          id="body-type-filter"
          value={body}
          onChange={(e) => setParam({ body: e.target.value })}
          className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
        >
          <option value="">Alle</option>
          {bodyOptions.map((o) => (
            <option key={o.slug} value={o.slug}>{o.label}</option>
          ))}
        </select>
      </div>
      <div>
        <label className="mb-1 block text-sm font-medium text-foreground">Prisrange</label>
        <Slider
          min={priceExtent.min}
          max={priceExtent.max}
          step={5000}
          value={[activeMin, activeMax]}
          onValueChange={(v) => setParam({ priceMin: v[0], priceMax: v[1] })}
          className="mt-3"
        />
        <div className="mt-2 flex justify-between text-xs text-muted-foreground">
          <span>{formatPrice(activeMin)} kr</span>
          <span>{formatPrice(activeMax)} kr</span>
        </div>
      </div>
      <div>
        <label className="mb-1 block text-sm font-medium text-foreground">Km-range</label>
        <Slider
          min={mileageExtent.min}
          max={mileageExtent.max}
          step={5000}
          value={[activeKmMin, activeKmMax]}
          onValueChange={(v) => setParam({ kmMin: v[0], kmMax: v[1] })}
          className="mt-3"
        />
        <div className="mt-2 flex justify-between text-xs text-muted-foreground">
          <span>{formatPrice(activeKmMin)} km</span>
          <span>{formatPrice(activeKmMax)} km</span>
        </div>
      </div>
      <div>
        <label className="mb-1 block text-sm font-medium text-foreground">Årgang</label>
        <Slider
          min={yearExtent.min}
          max={yearExtent.max}
          step={1}
          value={[activeYearMin, activeYearMax]}
          onValueChange={(v) => setParam({ yearMin: v[0], yearMax: v[1] })}
          className="mt-3"
        />
        <div className="mt-2 flex justify-between text-xs text-muted-foreground">
          <span>{activeYearMin}</span>
          <span>{activeYearMax}</span>
        </div>
      </div>
      {layout === "grid" && onReset && (
        <div className="flex items-end">
          <button
            onClick={onReset}
            className="rounded-lg border border-border bg-card px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
          >
            Nulstil filtre
          </button>
        </div>
      )}
    </div>
  );
}
